import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import AddAccountModal from './AddAccountModal';

import { toast } from 'react-toastify';
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";

export default function ChartOfAccountsPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const fetchTree = () => {
    setLoading(true);
    chartOfAccountsApi.getTree()
      .then(res => setTree(res.data || []))
      .catch(err => console.error("Error fetching tree", err))
      .finally(() => setLoading(false));
  };

  const handleSeed = async () => {
    if(!window.confirm("هل أنت متأكد من استيراد دليل الحسابات الافتراضي؟ قد يستغرق ذلك بعض الوقت.")) return;
    setSeeding(true);
    try {
        await chartOfAccountsApi.seedStandard();
        toast.success("تم استيراد دليل الحسابات بنجاح");
        fetchTree();
    } catch (err) {
        console.error(err);
        const msg = err.response?.data?.message || err.message || "حدث خطأ أثناء الاستيراد";
        toast.error(msg);
    } finally {
        setSeeding(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  // Filter tree based on search
  const filterNodes = (nodes, term) => {
    if (!term) return nodes;
    
    return nodes.reduce((acc, node) => {
      // Check if current node matches
      const matches = 
        node.code.toLowerCase().includes(term.toLowerCase()) || 
        node.nameAr.toLowerCase().includes(term.toLowerCase()) ||
        (node.nameEn && node.nameEn.toLowerCase().includes(term.toLowerCase()));
      
      // Check children
      const filteredChildren = node.children ? filterNodes(node.children, term) : [];
      
      if (matches || filteredChildren.length > 0) {
        // If children matched, auto expand
        if (filteredChildren.length > 0) {
            setExpandedNodes(prev => ({ ...prev, [node.id]: true }));
        }

        acc.push({
          ...node,
          children: filteredChildren
        });
      }
      
      return acc;
    }, []);
  };

  const displayedTree = React.useMemo(() => filterNodes(tree, searchTerm), [tree, searchTerm]);

  const toggleNode = (id) => {
    setExpandedNodes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const renderNode = (node) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];

    return (
      <div key={node.id} className="mr-4 border-r border-gray-300 pr-2">
        <div className="flex items-center py-1 hover:bg-gray-50 rounded group">
          <button 
            onClick={() => toggleNode(node.id)}
            className={`w-6 h-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-200 transition ${hasChildren ? 'visible' : 'invisible'}`}
          >
            {isExpanded ? '-' : '+'}
          </button>
          <div className="flex-1 flex items-center gap-2 pr-2">
             <span className={`font-mono text-sm ${node.level === 1 ? 'font-bold text-indigo-700' : 'text-gray-600'}`}>{node.code}</span>
             <span className={`font-medium ${node.level === 1 ? 'text-lg' : 'text-base'}`}>{node.nameAr}</span>
             <span className="text-xs text-gray-400">({node.level})</span>
             {node.allowPosting && <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">حساب فرعي</span>}
          </div>
        </div>
        
        {hasChildren && isExpanded && (
          <div className="pr-4">
            {node.children.map(renderNode)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 bg-white min-h-screen" dir="rtl">
      <div className="flex justify-between items-center mb-6 border-b pb-4">
        <div>
           <h1 className="text-2xl font-bold text-gray-800">دليل الحسابات</h1>
           <p className="text-gray-500 text-sm mt-1">عرض شجري للحسابات العامة والفرعية</p>
        </div>
        {hasPermission(PERMISSIONS.CHART_OF_ACCOUNTS_MANAGE) && (
          <div className="flex gap-2">
            <button 
                onClick={handleSeed}
                disabled={seeding}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded shadow flex items-center gap-2 border"
            >
                {seeding ? "جاري الاستيراد..." : "📥 استيراد الدليل الافتراضي"}
            </button>
            <button 
                onClick={() => setIsAddModalOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded shadow flex items-center gap-2"
            >
                <span>+</span> إضافة حساب فرعي
            </button>
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <input 
            type="text" 
            placeholder="بحث برقم الحساب أو الاسم..." 
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-indigo-500 outline-none"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-4 shadow-sm overflow-x-auto">
          {displayedTree.length > 0 ? displayedTree.map(renderNode) : <div className="text-center text-gray-500 py-10">لا توجد حسابات مطابقة للبحث</div>}
        </div>
      )}

      <AddAccountModal 
        isOpen={isAddModalOpen} 
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchTree}
      />
    </div>
  );
}
